import { applyMiddleware, combineReducers, createStore as createReduxStore, } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import createSagaMiddleware from 'redux-saga';
const SEED_REDUCERS_ACTION = { type: '@@internal/dynamicReduxStore/SEED_REDUCERS' };
const moduleAddedAction = (id) => ({
    type: '@@internal/dynamicReduxStore/MODULE_ADDED',
    payload: id,
});
const createDynamicMiddlewareManager = () => {
    let middlewares = [];
    const middleware = store => next => (action) => {
        const chain = middlewares.map(mw => mw(store));
        const dispatch = chain.reduceRight((nextDispatch, mw) => mw(nextDispatch), next);
        return dispatch(action);
    };
    return {
        middleware,
        addMiddlewares: (added) => {
            middlewares = [...middlewares, ...added];
        },
    };
};
const createCombinedReducer = (reducers, reducerCombiner) => (Object.keys(reducers).length === 0 ? state => (state !== null && state !== void 0 ? state : null) : reducerCombiner(reducers));
const createDynamicReducerManager = (initialReducerMap, reducerCombiner = combineReducers) => {
    let reducers = Object.assign({}, initialReducerMap);
    let combinedReducer = createCombinedReducer(reducers, reducerCombiner);
    return {
        reduce: (state, action) => combinedReducer(state, action),
        add: (key, reducer) => {
            if (!key || reducers[key]) {
                return;
            }
            reducers = Object.assign(Object.assign({}, reducers), { [key]: reducer });
            combinedReducer = createCombinedReducer(reducers, reducerCombiner);
        },
    };
};
const createModuleManager = (extensions, advancedCombineReducers) => {
    let dispatch;
    let reducerManager;
    const moduleIds = new Set();
    const middlewareManager = createDynamicMiddlewareManager();
    const reduce = (state, action) => reducerManager ? reducerManager.reduce(state, action) : (state !== null && state !== void 0 ? state : null);
    const add = (modules) => {
        const newlyAdded = [];
        modules.filter(Boolean).forEach(module => {
            if (moduleIds.has(module.id)) {
                return;
            }
            moduleIds.add(module.id);
            newlyAdded.push(module);
            if (module.reducerMap) {
                if (!reducerManager) {
                    reducerManager = createDynamicReducerManager(module.reducerMap, advancedCombineReducers);
                }
                else {
                    Object.entries(module.reducerMap).forEach(([key, reducer]) => reducerManager === null || reducerManager === void 0 ? void 0 : reducerManager.add(key, reducer));
                }
            }
            if (module.middlewares) {
                middlewareManager.addMiddlewares(module.middlewares);
            }
        });
        if (newlyAdded.length === 0) {
            return;
        }
        // Dispatch a no-op action so combineReducers seeds initial state for the newly added
        // reducers immediately, rather than waiting for the next arbitrary action to come through.
        dispatch === null || dispatch === void 0 ? void 0 : dispatch(SEED_REDUCERS_ACTION);
        newlyAdded.forEach(module => {
            var _a;
            extensions.forEach(extension => { var _a; return (_a = extension.onModuleAdded) === null || _a === void 0 ? void 0 : _a.call(extension, module); });
            dispatch === null || dispatch === void 0 ? void 0 : dispatch(moduleAddedAction(module.id));
            (_a = module.initialActions) === null || _a === void 0 ? void 0 : _a.forEach(action => dispatch === null || dispatch === void 0 ? void 0 : dispatch(action));
        });
    };
    return {
        reduce,
        add,
        middleware: middlewareManager.middleware,
        setDispatch: (nextDispatch) => {
            dispatch = nextDispatch;
        },
    };
};
/**
 * Configures a redux store that supports adding modules (reducers/middlewares/sagas) after
 * creation. Drop-in for `redux-dynamic-modules`'s `createStore`.
 */
export const createDynamicReduxStore = (settings, ...initialModules) => {
    const { initialState = {}, extensions = [], enhancers = [], advancedComposeEnhancers = composeWithDevTools({}), advancedCombineReducers, } = settings;
    const extensionMiddleware = extensions.flatMap(extension => { var _a; return (_a = extension.middleware) !== null && _a !== void 0 ? _a : []; });
    const moduleManager = createModuleManager(extensions, advancedCombineReducers);
    // Composing an arbitrary-length list of store enhancers isn't expressible in redux's own
    // `compose` overloads (same reason the upstream library's own types only special-case up to 8
    // modules); the result is a valid StoreEnhancer at runtime, just not one `compose`'s generics
    // can express here.
    const enhancer = advancedComposeEnhancers(...enhancers, applyMiddleware(...extensionMiddleware, moduleManager.middleware));
    const store = createReduxStore(moduleManager.reduce, initialState, enhancer);
    moduleManager.setDispatch(store.dispatch);
    const addModules = (modules) => moduleManager.add(modules);
    const addModule = (module) => addModules([module]);
    extensions.forEach(extension => { var _a; return (_a = extension.onModuleManagerCreated) === null || _a === void 0 ? void 0 : _a.call(extension, { addModule, addModules }); });
    store.addModule = addModule;
    store.addModules = addModules;
    store.dispose = () => extensions.forEach(extension => { var _a; return (_a = extension.dispose) === null || _a === void 0 ? void 0 : _a.call(extension); });
    addModules(initialModules);
    return store;
};
/**
 * Integrates redux-saga with the dynamic store: threads `sagaContext` into every saga's
 * `getContext()`/`setContext()` effects, and runs each module's sagas exactly once (deduped by
 * function reference), no matter how many modules reference the same saga. Drop-in for
 * `redux-dynamic-modules-saga`'s `getSagaExtension`.
 */
export const createSagaExtension = (sagaContext, onError) => {
    const sagaMonitor = process.env.NODE_ENV === 'development' && typeof window !== 'undefined'
        ? window.__SAGA_MONITOR_EXTENSION__
        : undefined;
    const sagaMiddleware = createSagaMiddleware({ context: sagaContext, sagaMonitor, onError });
    const runningSagaKeys = new Set();
    const runningSagaTasks = [];
    const runSaga = (registration) => {
        const task = typeof registration === 'function'
            ? sagaMiddleware.run(registration)
            : sagaMiddleware.run(registration.saga, registration.argument);
        runningSagaTasks.push(task);
    };
    return {
        middleware: [sagaMiddleware],
        onModuleAdded: module => {
            var _a;
            (_a = module.sagas) === null || _a === void 0 ? void 0 : _a.forEach(registration => {
                const key = typeof registration === 'function' ? registration : registration.saga;
                if (runningSagaKeys.has(key)) {
                    return;
                }
                runningSagaKeys.add(key);
                runSaga(registration);
            });
        },
        // Cancel every saga task started under this store. Load-bearing for SSR: a fresh store is
        // created per request, and without this every request's sagas would keep running forever.
        dispose: () => runningSagaTasks.forEach(task => task.cancel()),
    };
};
